// colDef/placementVendorColDef.tsx
import { TooltipText } from "@/components/ToolTip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  PlacementVendorStatus,
  PlacementVendorType,
} from "@/services/placementVendor.service";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";

const columnHelper = createColumnHelper<PlacementVendorType>();

export type PlacementVendorActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

const STATUS_CONFIG: Record<
  PlacementVendorStatus,
  { label: string; className: string }
> = {
  active: { label: "Active", className: "bg-gray-900 text-white" },
  inactive: { label: "Inactive", className: "bg-gray-100 text-gray-500" },
  pending: { label: "Pending", className: "bg-gray-100 text-gray-600" },
};

export const createPlacementVendorColumns = (
  onActionClick: (payload: PlacementVendorActionType) => void
) => [
  // Company Name — amber text (matching image)
  columnHelper.accessor("companyName", {
    header: "Company Name",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Contact Person — amber text (matching image)
  columnHelper.accessor("contactPerson", {
    header: "Contact Person",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Email — tooltip on long addresses
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <TooltipText text={info.getValue()} maxChars={30} className="text-sm" />
    ),
  }),

  // Total Placements — plain number
  columnHelper.accessor("totalPlacements", {
    header: "Total Placements",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Salary Package (LPA) — green with prefix (matching image)
  columnHelper.accessor("salaryPackage", {
    header: "Salary Package (LPA)",
    cell: (info) => (
      <span className="text-sm font-medium text-green-600">
        {info.getValue()} LPA
      </span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.companyName,
                action: "edit",
              })
            }
            aria-label="Edit vendor"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.companyName,
                action: "delete",
              })
            }
            aria-label="Delete vendor"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
